import { type Dispatch, type SetStateAction, useCallback } from 'react'

import { zodResolver } from '@hookform/resolvers/zod'
import { FormProvider, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router'
import { z } from 'zod'

import { Button } from '@/components/ui/button.tsx'
import { Dialog } from '@/components/ui/dialog.tsx'
import { useToast } from '@/components/ui/toast.tsx'
import { type TeamRole } from '@/modules/team/types'
import { AppRoutes } from '@/utils/constants'
import { handleError } from '@/utils/error'

import { useUpdateTeamRoleTable } from '../../../hooks/use-update-team-role'
import { TeamRoleForm } from '../team-role-form'

interface TeamRoleCreateDialogType {
  setShowCreateRoleDialog: Dispatch<SetStateAction<boolean>>
  shoCreateRoleDialog: boolean
}

export type CreateRoleFormValues = z.infer<typeof CreateRoleFormSchema>

const CreateRoleFormSchema = z.object({
  name: z.string().min(1, { message: 'Role name is required' }),
  isDefault: z.boolean(),
  memberIds: z.array(z.number()),
})

const inviteFormValues: CreateRoleFormValues = {
  name: '',
  isDefault: false,
  memberIds: [],
}

export const TeamRoleCreateDialog = ({
  setShowCreateRoleDialog,
  shoCreateRoleDialog,
}: TeamRoleCreateDialogType) => {
  const { createRole } = useUpdateTeamRoleTable()

  const toast = useToast()
  const navigate = useNavigate()

  const editForm = useForm<CreateRoleFormValues>({
    mode: 'onChange',
    defaultValues: inviteFormValues,
    resolver: zodResolver(CreateRoleFormSchema),
  })

  const { handleSubmit, reset } = editForm

  const handleClose = useCallback(() => {
    setShowCreateRoleDialog(false)
    reset()
  }, [reset, setShowCreateRoleDialog])

  const onSubmit = useCallback(
    async (values: CreateRoleFormValues) => {
      try {
        await createRole.mutateAsync(values).then((newRole: TeamRole) => {
          navigate(AppRoutes.Teams.Roles.Edit.asUrl(String(newRole.id)))
          toast.success(`Successfully created role ${values.name}`, {
            duration: 3000,
          })
        })
      } catch (error) {
        handleError(error, 'Failed to create role')
      } finally {
        handleClose()
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [createRole, handleClose, toast],
  )

  return (
    <Dialog
      show={shoCreateRoleDialog}
      onClose={handleClose}
      bigDialog={true}
    >
      <FormProvider {...editForm}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Dialog.Header>
            <Dialog.Title>Create new Role</Dialog.Title>
            <Dialog.Description className="text-center">
              Add a new role to your team
            </Dialog.Description>
          </Dialog.Header>
          <Dialog.Description>
            <TeamRoleForm />
          </Dialog.Description>
          <Dialog.ClosableFooter onClose={handleClose}>
            <Button
              type="submit"
              variant="default"
              className="w-full"
              isLoading={createRole.isLoading}
              disabled={!editForm.formState.dirtyFields.name}
            >
              Create
            </Button>
          </Dialog.ClosableFooter>
        </form>
      </FormProvider>
    </Dialog>
  )
}
